import { aiProcessesApi, aiProcessStepsApi } from 'app/services/api';
import _ from 'lodash';

import { PAGE_CODES } from 'constants/pageCodes';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { selectAiProcessById } from 'store/system/selectors/aiProcesses.selectors';

export const AI_PROCESSES_ACTION_TYPE = createNamedActions('aiProcesses', {
  GET_AI_PROCESSES: 'GET_AI_PROCESSES',
  GET_AI_PROCESS_BY_ID: 'GET_AI_PROCESS_BY_ID',
  UPDATE_AI_PROCESS: 'UPDATE_AI_PROCESS',
  CREATE_AI_PROCESS: 'CREATE_AI_PROCESS',
  DELETE_AI_PROCESS: 'DELETE_AI_PROCESS',
  GET_AI_PROCESS_STEPS: 'GET_AI_PROCESS_STEPS',
  UPDATE_AI_PROCESS_STEP: 'UPDATE_AI_PROCESS_STEP',
  CREATE_AI_PROCESS_STEP: 'CREATE_AI_PROCESS_STEP',
  DELETE_AI_PROCESS_STEP: 'DELETE_AI_PROCESS_STEP',
  GET_AI_PROCESS_STEPS_TOOLS: 'GET_AI_PROCESS_STEPS_TOOLS',
  GET_AI_PROCESS_STEPS_RESOURCES: 'GET_AI_PROCESS_STEPS_RESOURCES',
  UPDATE_AI_PROCESS_STEP_ORDER: 'UPDATE_AI_PROCESS_STEP_ORDER',
  UPDATE_AI_PROCESS_STEP_ORDER_PENDING: 'UPDATE_AI_PROCESS_STEP_ORDER_PENDING',
  UPDATE_AI_PROCESS_STEP_ORDER_REJECTED: 'UPDATE_AI_PROCESS_STEP_ORDER_REJECTED',
  GET_AI_PROCESS_FORM_SCHEMA: 'GET_AI_PROCESS_FORM_SCHEMA',
  UPDATE_AI_PROCESS_FORM_SCHEMA: 'UPDATE_AI_PROCESS_FORM_SCHEMA',
  DELETE_AI_PROCESS_FORM_SCHEMA: 'DELETE_AI_PROCESS_FORM_SCHEMA',
  GET_GLOBAL_AI_PROCESSES: 'GET_GLOBAL_AI_PROCESSES',
  ENABLE_GLOBAL_AI_PROCESS: 'ENABLE_GLOBAL_AI_PROCESS',
  DISABLE_GLOBAL_AI_PROCESS: 'DISABLE_GLOBAL_AI_PROCESS',
  CLONE_GLOBAL_AI_PROCESS: 'CLONE_GLOBAL_AI_PROCESS',
  RESET_AI_PROCESSES_STATE: 'RESET_AI_PROCESSES_STATE',
});

// GET /ai-processes is guarded by the AI service's View policy (view | edit | manage); skip the
// request for users without it so pages that list processes as a side dependency don't 403.
export const getAiProcessesAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const { isViewAllowed } = getMasterPermissionsToPage(PAGE_CODES.AI_PROCESSES)(getState());

  if (!isViewAllowed) {
    return dispatch({
      type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESSES,
      payload: [],
    });
  }

  const { data } = await aiProcessesApi.getAiProcesses(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESSES,
    payload: data,
  });
});

export const updateAiProcessAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessesApi.updateAiProcess(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS,
    payload: data,
  });
});

export const createAiProcessAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessesApi.createAiProcess(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.CREATE_AI_PROCESS,
    payload: data,
  });
});

export const deleteAiProcessAction = catchAsyncActionError(id => async dispatch => {
  await aiProcessesApi.deleteAiProcess(id);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS,
    payload: id,
  });
});

export const getAiProcessStepsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessStepsApi.getAiProcessSteps(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS,
    payload: { aiProcessId: params.aiProcessId, steps: data },
  });
});

export const updateAiProcessStepAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessStepsApi.updateAiProcessStep(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP,
    payload: { aiProcessId: params.aiProcessId, step: data },
  });
});

export const createAiProcessStepAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessStepsApi.createAiProcessStep(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.CREATE_AI_PROCESS_STEP,
    payload: { aiProcessId: params.aiProcessId, step: data },
  });
});

export const deleteAiProcessStepAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    await aiProcessStepsApi.deleteAiProcessStep(params);

    dispatch({
      type: AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS_STEP,
      payload: params,
    });

    const aiProcess = selectAiProcessById(params.aiProcessId)(getState());
    const remainingSteps = _.orderBy(aiProcess?.steps ?? [], 'orderNumber', 'asc');
    const stepsToUpdate = remainingSteps
      .map((step, index) => ({
        id: step.id,
        orderNumber: index + 1,
        originalOrderNumber: step.orderNumber,
      }))
      .filter(({ orderNumber, originalOrderNumber }) => orderNumber !== originalOrderNumber);

    if (!stepsToUpdate.length) {
      return null;
    }

    return dispatch(
      updateAiProcessStepOrderAction({ aiProcessId: params.aiProcessId, stepsToUpdate }),
    );
  },
);

export const getAiProcessStepToolsAction = catchAsyncActionError(scopeKind => async dispatch => {
  const { data } = await aiProcessStepsApi.getAiProcessStepsTools(scopeKind);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS_TOOLS,
    payload: data,
  });
});

export const getAiProcessStepResourcesAction = catchAsyncActionError(
  scopeKind => async dispatch => {
    const { data } = await aiProcessStepsApi.getAiProcessStepsResources(scopeKind);

    return dispatch({
      type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS_RESOURCES,
      payload: data,
    });
  },
);

export const updateAiProcessStepOrderAction = ({ aiProcessId, stepsToUpdate }) =>
  catchAsyncActionError(() => async dispatch => {
    const filteredBody = stepsToUpdate.map(({ id, orderNumber }) => ({ id, orderNumber }));

    await aiProcessStepsApi.updateAiProcessStepOrder(aiProcessId, filteredBody);

    return dispatch({
      type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER,
    });
  })({
    pendingAction: () => ({
      type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER_PENDING,
      payload: { aiProcessId, stepsToUpdate },
    }),
    rejectedAction: () => ({
      type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER_REJECTED,
      payload: { aiProcessId, stepsToUpdate },
    }),
  });

export const getAiProcessFormSchemaAction = catchAsyncActionError(aiProcessId => async dispatch => {
  const { data } = await aiProcessesApi.getAiProcessFormSchema(aiProcessId);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_FORM_SCHEMA,
    payload: { aiProcessId, formSchema: data ?? null },
  });
});

export const updateAiProcessFormSchemaAction = catchAsyncActionError(
  ({ aiProcessId, formSchema }) =>
    async dispatch => {
      const { data } = await aiProcessesApi.updateAiProcessFormSchema(aiProcessId, formSchema);

      return dispatch({
        type: AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_FORM_SCHEMA,
        payload: { aiProcessId, formSchema: data ?? null },
      });
    },
);

export const deleteAiProcessFormSchemaAction = catchAsyncActionError(
  aiProcessId => async dispatch => {
    await aiProcessesApi.deleteAiProcessFormSchema(aiProcessId);

    return dispatch({
      type: AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS_FORM_SCHEMA,
      payload: { aiProcessId },
    });
  },
);

export const getGlobalAiProcessesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiProcessesApi.getAiProcessTemplates(params);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.GET_GLOBAL_AI_PROCESSES,
    payload: data,
  });
});

export const enableGlobalAiProcessAction = catchAsyncActionError(id => async dispatch => {
  await aiProcessesApi.enableAiProcessTemplate(id);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.ENABLE_GLOBAL_AI_PROCESS,
    payload: id,
  });
});

export const disableGlobalAiProcessAction = catchAsyncActionError(id => async dispatch => {
  await aiProcessesApi.disableAiProcessTemplate(id);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.DISABLE_GLOBAL_AI_PROCESS,
    payload: id,
  });
});

export const cloneGlobalAiProcessAction = catchAsyncActionError(id => async dispatch => {
  const { data } = await aiProcessesApi.cloneAiProcessTemplate(id);

  return dispatch({
    type: AI_PROCESSES_ACTION_TYPE.CLONE_GLOBAL_AI_PROCESS,
    payload: data,
  });
});

export const resetAiProcessesStateAction = () => ({
  type: AI_PROCESSES_ACTION_TYPE.RESET_AI_PROCESSES_STATE,
});
